import { Config, Helper } from 'config';

import { forEach, map, find, every, some, compact } from 'lodash';

import { OwnedObject, concat, cloneDeep,
         resolve, invokeMap$, map$ } from 'gear';

import { Xml } from '../util';
import { Point, Matrix, Bounds } from '../geometry';

import { Processor, GroupProcessor, updateCache } from './detail/processor';
import { Figure } from './figure';
import { Figures } from './figures';
import { GShape } from './shape';
import { PowerClipLink } from './power-clip';
import { AbsoluteAnchor } from './anchor';

const consumePolyregionsIntoGShape = function(polyregions,primitives) {
  if( polyregions.length > 0 ) {
    const gs = new GShape({ polyregions: polyregions });
    polyregions.splice(0,polyregions.length);
    primitives.push( gs );
  }
};

const extractPolyregionsAndRasters = function( figures,parentMatrix,polyregions,primitives) {
  forEach( figures, f => {

    if( f.isRaster ) {
      consumePolyregionsIntoGShape(polyregions,primitives);

      const r = f.clone();
      if( ! parentMatrix.isIdentity() ) {
        r.transform(parentMatrix);
      }
      primitives.push(r);
      return;
    }

    // Shape

    const prePin = f._computeInitialPrePin();
    const figureMatrix =  f._computeMatrix(prePin);
    const cm = Matrix.m( parentMatrix, figureMatrix );
    const pcMatrix = Matrix.m(cm,Matrix.translate(prePin));

    if( f.isGroup ) {
      extractPolyregionsAndRasters( f.figures, cm, polyregions, primitives );
    }
    else {
      const f_polyregions = f._polyregions,
          f_powerClipContents = f._powerClipContents;

      const c_f_polyregions = cloneDeep(f_polyregions);

      forEach( c_f_polyregions, p => {
        const pcLink = p._powerClipLink;
        if( pcLink ) { // The polyregion could already have the pcLink
          // console.log('Transform PC-Link: ' + pcMatrix.textualize() + ' , prePin: ' + prePin.textualize() );
          pcLink.transform( pcMatrix );
        }
        else {
          const pcId = p._powerClipId;
          if( pcId ) {
            const pcContent = find(f_powerClipContents,{ _id: pcId });
            if( pcContent ) {
              // console.log('Build PC-Link: ' + pcMatrix.textualize() + ' , prePin: ' + prePin.textualize() );
              p._powerClipLink = new PowerClipLink({ content: pcContent, matrix: pcMatrix });
            }
          }
        }
      });

      forEach( map(c_f_polyregions, cp => {
        if( ! cm.isIdentity() ) {
          // console.log('Transform polyregion: '+ cp._powerClipId + ' '+cm.textualize());
          cp.transform(cm);
          const pen = cp.pen;
          if (pen){
            pen._width *= cm.scaleFactor(); 
          }
          }
        return cp;
      }), pc => {
        polyregions.push(pc);
      });
    }
  });
};

export const _toPrimitives = function( figures ) {
  const primitives = [], polyregions = [];
  extractPolyregionsAndRasters( figures, new Matrix(), polyregions, primitives );
  consumePolyregionsIntoGShape( polyregions, primitives );
  return primitives;
};

const _canUseGroupProcessor = function( figures ) {
  // return _.every( filter(figures,'isGroup')
  //               , function(group) { return group.canUseGroupProcessor(); });
  for(let k=0,kEnd=figures.length;k<kEnd;++k) {
    const figure = figures[k];
    if( figure.isGroup && ! figure.canUseGroupProcessor() ) {
      return false;
    }
  }
  return true;
};

// [TODO] Take into account Anchor, for now this is only working for <Anchor X="0" Y="0"/>
Figure._group = function( figure, parent, source = figure ) {
  if( parent.matrix22.isIdentity() ) {
    // Simple group, no need to change the figures matrix. We can keep the metrics
    // and avoid proessing server calls for proxyGroups
    figure.translate( Point.minus( parent.pin ) );
    return figure;
  }
  else {
    const matrix22 = source.matrix22;
    const invertedParentMatrix22 = parent.matrix22.inverted();
    figure.pin = invertedParentMatrix22.apply( source.pin .minus( parent.pin ) );
    figure.matrix22 = Matrix.m( invertedParentMatrix22, matrix22 );
    return figure;
  }
};

// We only enter here when the parent trasnform is the identity, and we
// are regrouping into a top group.
// We could optimize further here. It is to complex for the moment, we may
// want to explore better abstractions

const stashGlobalMetrics = function( figure ) {
  figure.deepForEachFigure( cf => {
    const cache = cf._cache;
    cache.stash('globalMatrix'        );
    cache.stash('globalVectorBounds'  );
    cache.stash('globalBounds'        );
    cache.stash('globalMaterialBounds');
  });
};

const unstashGlobalMetrics = function( figure ) {
  figure.deepForEachFigure( cf => {
    const cache = cf._cache;

    cache.copy('globalMatrix'      ,'globalPreMatrix'      );
    cache.copy('globalVectorBounds','globalPreVectorBounds');
    cache.copy('globalBounds'      ,'globalPreBounds'      );

    cache.unstash('globalMatrix'        );
    cache.unstash('globalVectorBounds'  );
    cache.unstash('globalBounds'        );
    cache.unstash('globalMaterialBounds');
  });
};

Figure._fastGroup = function(figure, parent ) {

  stashGlobalMetrics(figure);

  figure.translate( Point.minus( parent.pin ) );

  unstashGlobalMetrics(figure);

  return figure;
};

Figure._grouped = function( figure, parent ) {
  return Figure._group( figure.clone(), parent );
};

Figure._ungroup = function( figure, parent, source = figure ) {
  const matrix22 = source.matrix22;
  const parentMatrix22 = parent.matrix22;
  var parentPin = parent.pin;
  if (parent.anchor.absolute){
    parentPin = parentPin.minus(parentMatrix22.apply(parent.anchor.pos));
  }
  else
  {
    const bounds = parent.preBounds_();
    parentPin = parentPin.minus(bounds.interpolate(parent.anchor.pos.x,parent.anchor.pos.y));
  }
  figure.pin = parentMatrix22.apply( source.pin ) .plus( parentPin );
  figure.matrix22 = Matrix.m( parentMatrix22, matrix22 );
  figure.invalidate();
  return figure;
};
Figure._ungrouped = function( figure, parent ) {
  return Figure._ungroup( figure.clone(), parent );
};


export const Group = Figure.Group = Figure.extend({ typeName: 'Group',

  type: 'Group',

  Properties: {
    figures: { type: 'Figure[]', def: [],
      onChange() { this.invalidate(); },
      manualParsing: true,
      manualCloning: true
    }
  },

  get isGroup() {
    return true;
  },

  invalidate() {
    this._updateProcessor();
    return this.callParent();
  },

  defaultAnchor() {
    return AbsoluteAnchor(new Point(0,0));
  },

  topColors() {}, // override, no top colors in group
  otherColors( colors) {
    const figures = this._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].getColors(colors);
    }//);
    return colors;
  },

  otherPens( pens) {
    const figures = this._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].getPens(pens);
    }//);
    return pens;
  },

  topBrushes() {}, // override, no top brushes in group
  otherBrushes( brushes) {
    const figures = this._figures;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      figures[k].getBrushes(brushes);
    }//);
    return brushes;
  },

  setupXml( node ) {

    const figuresNode = Xml.appendElement(node,'Figures') ;
    Xml.addObjectArray(figuresNode,this._figures);

    this.callParent(node);
  },

  canEditContent() { return this._processes.length === 0; },

  canBeProcessedLocally( figures ) {
    return this.processesAreSupported() &&
      every( figures || this.figures, f => f.canBeProcessedLocally() );
    /*
    return this._processes.length === 0 && _.every(figures||this._figures,f=> {
      // We have to review this pattern. Right now the Processor code doesn't support local processing
      // of groups that has text on them. This will be changed later.
      return f.canBeProcessedLocally() && ! ( f.isText );
    });*/
  },

  _representationType() {
    return ( this.needsCacheToRender() || ! this.canBeProcessedLocally() ) ? 'server' : 'local';
  },

  processesAreSupported() {
    const { processes } = this;
    return processes.length === 0 || every( processes, process => process.canProcess(this) );
  },

  onlySimpleShapes() {
    return this.processes.length === 0 && every( this.figures, figure => {
      if( figure.isShape ) {
        return figure.processes.length === 0 && figure.canBeProcessedLocally();
      }
      if( figure.isGroup ) {
        return figure.onlySimpleShapes();
      }
      return false;
    });
  },

  hasPreview() {
    return false;
  },

  deepPowerClipContents() {
    let pccs = this._powerClipContents;
    const { figures } = this;
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      pccs = concat(pccs,figures[k].deepPowerClipContents());
    }
    return pccs;
  },

  needsCacheToRender() {
    return some( this.figures, f => f.needsCacheToRender() );
  },

// Disable bounds using preBounds optimization
  _optimizeVectorBounds() {
    return null;
  },
  _optimizeBounds() {
    return null;
  },

  _fineHitTest( point, transform, options ) {
    for( let k = this.figures.length - 1, kEnd = 0; k >= kEnd; --k ) {
      const figure = this.figures[k];
      if( figure._fineHitTest(point, figure.globalMatrix_(), options))
      {
        return true;
      }
    }
    return false;
  },

  _computePreReferencePoint() {
    return this._figures[0].referencePoint_() ;
  },

  _primitive_computePreVectorBounds() {
    // console.log('Group._primitive_computePreVectorBounds');
    return Bounds.union( map( this.figures, f => {
      return f._primitive_computeVectorBounds();
    }));
  },
  _primitive_computePreBounds() {
    // console.log('Group._primitive_computePreBounds');
    return Bounds.union( map( this.figures, f => {
      return f._primitive_computeBounds();
    }));
  },

  _primitive_computePreVectorBoundsAfter( transform ) {
    // console.log('Group._primitive_computePreVectorBoundsAfter');
    return Bounds.union( map( this.figures, f => {
      return f._primitive_computePreVectorBoundsAfter(Matrix.m(transform,f.matrix_()));
    }));
  },
  _primitive_computePreBoundsAfter( transform ) {
    // console.log('Group._primitive_computePreBoundsAfter');
    return Bounds.union( map( this.figures, f => {
      return f._primitive_computePreBoundsAfter(Matrix.m(transform,f.matrix_()));
    }));
  },

  _primitive_computePreMaterialBoundsAfter: function(transform) {
    // console.log('Group._primitive_computePreMaterialBoundsAfter');
    return Bounds.union( compact( map( this.figures, f => {
      return f._primitive_computePreMaterialBoundsAfter(Matrix.m(transform,f.matrix_()));
    })));
  },

  _prepareToDraw$( zoom ) {
    return invokeMap$(this.figures,'_prepareToDraw$',zoom);
  },

  prepareToDraw$( zoom ) {
    // Ask for a bigger image if need it
    if( this._processor instanceof GroupProcessor ) {
      return invokeMap$(this.figures,'prepareToDraw$',zoom);
    }
    else {
      // [TODO] this.callParent(zoom);
      // Ask for a bigger image if need it
      const outputFigure = this._outputFigure;
      if( outputFigure ) {
        return outputFigure._prepareToDraw$(zoom);
      }
      else {
        // Check, if we should call update$ here
        return resolve(zoom);
      }
    }
  },

  _preparedToDraw( zoom ) {
    return every( this.figures, f => f._preparedToDraw(zoom) );
  },

  preparedToDraw( zoom ) {
    // Ask for a bigger image if need it
    if( this._processor instanceof GroupProcessor ) {
      return Figures.preparedToDraw(this._figures,zoom);
    }
    else {
      return this.callParent(zoom);
    }
  },

  drawForClipping( context, transform ) {
    const { figures } = this;
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      const f = figures[k];
      f.drawForClipping(context, transform);
    }
  },

  _draw( context, transform, config ) {
    const { figures } = this;
    //_.forEach(figures,function(f) {
    for(let k=0,kEnd=figures.length;k<kEnd;++k) {
      const f = figures[k];
      const output = f.outputFigure || f;

      const alpha = output._alpha;
      const changeGlobalAlpha = ( alpha !== 1.0 );
      if( changeGlobalAlpha ) {
        context.save();
        context.globalAlpha *= alpha;
      }

      output._draw(context,transform,config);

      if( changeGlobalAlpha ) {
        context.restore();
      }
    }//);
  },

  init( c = {} ) {
    this.callParent(c);

    // [TODO] see below
    if( !c || ( ! c.anchor && ! c._anchor ) ) {
      this._anchor = AbsoluteAnchor(new Point(0,0));
    }

    if( ! c.noProcessor ) {
      this._updateProcessor();
    }
  },

  ungroup( figures ) {
    if( figures === undefined ) {
      figures = this._figures.clone();
    }
    this._figures.remove( figures );
    forEach( figures, figure => {
      Figure._ungroup(figure,this);
    });
    return figures;
  },

  get ungroupedFigures() {
    return map( this.figures, figure => Figure._ungrouped(figure,this) );
  },

  regroup( figures ) {
    this._updateProcessor(figures);

    const isTopGroup = ! this.isGrouped;
    const fastGrouping = this.matrix22.isIdentity() && isTopGroup;

    let owner = this;
    while( owner && owner.isGroup ) {
      owner._cache.clear();
      owner = owner._owner;
    }

    const groupedFigures = map( figures, figure => {
      const gf = OwnedObject.adoptObject( figure, null );
      if( fastGrouping ) {

        Figure._fastGroup(gf,this);
      }
      else{
        Figure._group(gf,this);

        // Clear the metrics by hand, we need a revision of how we are handling
        // invalidation of subfigures for this to work correctly
        updateCache(gf,{});
      }

      return gf;

    });

    const this_figures = this._figures;

    if( this._processor instanceof Processor ) {
      this_figures.set(groupedFigures);
    }
    else {
      this_figures._set(groupedFigures); // avoid events
      if( ! fastGrouping ) {
        this.invalidateGrouping();
      }
    }

    return this;
  },

  _clone( partOfGroup,ungrouping) {
    const p = this.callParent();
    p.figures = map( this.figures, f => f.clone(true,partOfGroup,ungrouping) );
    return p;
  },

  supportsLocalMetrics() {
    return every( this.figures, f => f.supportsLocalMetrics() );
  },

  containsRasters( figures ) {
    return some( figures|| this.figures, f => f.isRaster || ( f.isGroup && f.containsRasters() ) );
  },

  containsLinkedResources() {
    return !! Figures.deepFindFigureLeave( this, figure => figure.containsLinkedResources() );
  },

  canUseGroupProcessor( figures ) {
    return this._anchor._absolute && _canUseGroupProcessor( figures || this.figures );
  },

  _updateProcessor( figures ) {

    const useGroupProcessor = Config.enablePerFigureBasedGroupRendering &&
                              Config.computeFigureGeometryLocally &&
                              this.canUseGroupProcessor(figures) &&
                              this.processes.length === 0 &&
                              // Use a common processor for simple grouped shapes
                              // We can allow a common processor for grouped rasters too. We need to think
                              // if this is a good strategy, it could be faster for overlay or background
                              // rasters combined with shapes
                              ! ( this.canBeProcessedLocally(figures) && ! this.containsRasters(figures) ) &&
                              ! ( Helper.isIE_let(10) && this._xType === "System.Clipart" );
    if( useGroupProcessor ) {
      if( ! (this._processor instanceof GroupProcessor) ) {
        // console.log('using group processor');
        // Every figure is processed on his own
        this._processor = new GroupProcessor(this);
      }
    }
    else {
      if( ! (this._processor instanceof Processor) ) {
        // console.log('using normal processor');
        // The whole group is processed as one figure
        this._processor = new Processor(this);
      }
    }
  },

  _computeInitialPrePin() {
    return this._anchor.pos.clone(); // Always absolute for groups
  },

  inputPrimitiveFigures$() {

    return map$( this.figures, f => {
      if( f.isShape && f._processes.length === 0 ) {
        f.outputFigure = f;
        return f;
      }
      else {
        return f.update$().then( () => {
        return f.outputFigure;
        });
      }
    }).then( processedFigures => {

      const prePin = this._computeInitialPrePin();

      const figures = _toPrimitives(processedFigures);

      return { figures, anchor: prePin };
    });
  }
});
// [TODO] We need this...
// Group.prototype.Properties.anchor.def = AbsoluteAnchor( new Point(0,0) );

Xml.addFromBuild(Group,'Group');

Group.parseXml = function (node,bcFixes) {
  const group = new Group();

  Figure._baseParseXml.call(group, node, Group, bcFixes);

  const figuresNode = Xml.child(node,'Figures');
  const figures = figuresNode ? Figure.arrayFromXml(figuresNode,bcFixes) : [];
  group.figures = figures;

  if(!group.figures || !group.figures.length) return null;

  return group;
};

Xml.Parser['Group']   = Xml.makeObjectParser(Group);
Xml.Parser['Group[]'] = Xml.makeArrayObjectParser(Group);

Group.create = function( figures ) {
  const group = new Group({ noProcessor: true });

  // We are not going to define a composed matrix for the group anymore
  // These has two benefits: frame is aligned to x-y and metrics do not need to be recomputed
  // group.matrix22 = Figures.composedIsometricMatrix22(figures);
  group.pin = Figures.meanPin(figures);

  group.regroup( figures );

  return group;
};



/** WEBPACK FOOTER **
 ** ./src/cdl/figure/group.js
 **/